import { useState, useEffect } from 'react';
import { useAddressAutocomplete } from '@/hooks/useAddressAutocomplete';
import { FEATURE_FLAGS } from '@/config/features';

interface Address {
  id?: number;
  name: string;
  street: string;
  city: string;
  state: string;
  zipCode: string;
  isDefault?: boolean;
}

interface AddressFormProps {
  address?: Partial<Address>;
  onSave: (address: Partial<Address>) => void;
  onCancel: () => void;
  showDefaultOption?: boolean;
  submitButtonText?: string;
  cancelButtonText?: string;
  isLoading?: boolean;
}

export function AddressForm({ 
  address = {}, 
  onSave, 
  onCancel, 
  showDefaultOption = false,
  submitButtonText = "Save Address",
  cancelButtonText = "Cancel",
  isLoading = false
}: AddressFormProps) {
  const [formData, setFormData] = useState({
    name: address.name || '',
    street: address.street || '',
    city: address.city || '',
    state: address.state || '',
    zipCode: address.zipCode || '',
    isDefault: address.isDefault || false,
  });

  const { suggestions, loading, searchAddress, clearSuggestions } = useAddressAutocomplete();
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchQuery, setSearchQuery] = useState(address.street || '');
  const [shouldSearch, setShouldSearch] = useState(!address.street); // Don't search on initial load if address exists

  // Debounced search effect - triggers search 500ms after user stops typing
  useEffect(() => {
    if (!FEATURE_FLAGS.ENABLE_ADDRESS_AUTOCOMPLETE) {
      return; // Skip autocomplete if feature is disabled
    }

    if (!shouldSearch) {
      setShouldSearch(true); // Reset the flag for next time
      return;
    }

    const timeoutId = setTimeout(() => {
      if (searchQuery.length >= 3) {
        searchAddress(searchQuery);
        setShowSuggestions(true);
      } else if (searchQuery.length > 0) {
        clearSuggestions();
        setShowSuggestions(false);
      }
    }, 500); // 500ms delay

    return () => clearTimeout(timeoutId);
  }, [searchQuery]); // Only depend on searchQuery

  const handleStreetChange = (value: string) => {
    setFormData({ ...formData, street: value });
    setSearchQuery(value);
    
    // Clear suggestions immediately if input is empty
    if (value.length === 0) {
      clearSuggestions();
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (suggestion: any) => {
    setFormData({
      ...formData,
      street: suggestion.street,
      city: suggestion.city,
      state: suggestion.state,
      zipCode: suggestion.zip_code,
    });
    setShouldSearch(false); // Prevent search when programmatically setting value
    setSearchQuery(suggestion.street); // Update search query to match selected street
    setShowSuggestions(false);
    clearSuggestions();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({ ...address, ...formData });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Address Section Header */}
      <div className="border-b border-gray-200 pb-2">
        <h3 className="text-lg font-semibold text-gray-900">Address Information</h3>
      </div>
      
      {/* Address Name - Full Width Row */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Address Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
          placeholder="e.g., Home, Work, Mom's House"
          required
        />
      </div>

      {/* Address Fields - Two Column Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="relative">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Street Address <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.street}
            onChange={(e) => handleStreetChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
            placeholder="123 Main St"
            required
          />
          
          {/* Autocomplete Suggestions */}
          {showSuggestions && (
            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
              {loading ? (
                <div className="px-3 py-2 text-sm text-gray-500 flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-500 mr-2"></div>
                  Searching addresses...
                </div>
              ) : suggestions.length > 0 ? (
                suggestions.map((suggestion, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => handleSuggestionClick(suggestion)}
                    className="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm"
                  >
                    {suggestion.display_name}
                  </button>
                ))
              ) : searchQuery.length >= 3 ? (
                <div className="px-3 py-2 text-sm text-gray-500">
                  No addresses found
                </div>
              ) : null}
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            City <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.city}
            onChange={(e) => setFormData({ ...formData, city: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
            placeholder="New York"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            State <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.state}
            onChange={(e) => setFormData({ ...formData, state: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
            placeholder="NY"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Zip Code <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.zipCode}
            onChange={(e) => setFormData({ ...formData, zipCode: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
            placeholder="10001"
            required
          />
        </div>

        {showDefaultOption && (
          <div className="flex items-center">
            <input
              type="checkbox"
              id="is_default"
              checked={formData.isDefault}
              onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
              className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
            />
            <label htmlFor="is_default" className="ml-2 text-sm text-gray-700">
              Set as default address
            </label>
          </div>
        )}
      </div>

      <div className="flex space-x-3">
        <button
          type="submit"
          disabled={isLoading}
          className={`px-4 py-2 rounded-lg font-medium focus:outline-none focus:ring-2 cursor-pointer ${
            isLoading
              ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
              : 'bg-green-600 text-white hover:bg-green-700 focus:ring-green-500'
          }`}
        >
          {isLoading ? (
            <span className="flex items-center justify-center gap-2">
              <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
              Saving...
            </span>
          ) : (
            submitButtonText
          )}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={isLoading}
          className={`px-4 py-2 rounded-lg font-medium focus:outline-none focus:ring-2 cursor-pointer ${
            isLoading
              ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300 focus:ring-gray-500'
          }`}
        >
          {cancelButtonText}
        </button>
      </div>
    </form>
  );
}